/**
 * Jobs — Job Progress (every sold job, after the Deal is Closed Won).
 *
 * Live: n8n "[C] App 13 - Jobs" (REACT_APP_JOBS_URL), Zoho "Jobs" module:
 *   list     every job → [{ id, name, stage, planStatus, step, agent, address, total, paid, balance, … }]
 *   detail   one job   → { job: { …, plan, planSummary }, notes }
 * Jobs are created by n8n "[C] Jobs 1 - Closed Won → Job" — the app never creates them.
 * Test mode (no URL): no jobs.
 */
import { useSyncExternalStore } from 'react';
import { getAccessToken } from './auth';

const JOBS_URL = process.env.REACT_APP_JOBS_URL;
export const JOBS_LIVE = !!JOBS_URL;

const call = async (body) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to see jobs');
  let res;
  try {
    res = await fetch(JOBS_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('No connection — try again');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `Couldn’t reach Zoho (${res.status})`);
  return out;
};

/* ---------- where a job sits on the two pages ---------- */
// In Progress: everything not finished. "Needs setup / approval" until admin approves the plan.
export const needsApproval = (j) => j.stage !== 'Cancelled' && j.stage !== 'Completed' && j.planStatus !== 'Approved';
export const pageOf = (j) => (j.stage === 'Completed' || j.stage === 'Cancelled' ? 'completed' : 'progress');

/* ---------- the list (one copy for both pages) ---------- */
let state = { jobs: [], status: 'idle', error: '' };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const refreshJobs = async () => {
  if (!JOBS_LIVE) return;
  state = { ...state, status: 'loading', error: '' };
  emit();
  try {
    const out = await call({ action: 'list' });
    state = { jobs: out.jobs || [], status: 'ready', error: '' };
  } catch (err) {
    state = { ...state, status: 'error', error: err.message || 'Couldn’t load jobs' };
  }
  emit();
};

export const useJobs = () => useSyncExternalStore(subscribe, () => state);

/* ---------- one job (kept per job, so going back and forth is instant) ---------- */
const details = new Map(); // id → { data, loading, error }

export const loadJob = async (id) => {
  if (!id || !JOBS_LIVE) return;
  const prev = details.get(id) || {};
  details.set(id, { ...prev, loading: true, error: '' });
  emit();
  try {
    const out = await call({ action: 'detail', jobId: id });
    details.set(id, { data: { job: out.job, notes: out.notes || [] }, loading: false, error: '' });
  } catch (err) {
    details.set(id, { ...prev, loading: false, error: err.message || 'Couldn’t load this job' });
  }
  emit();
};

const EMPTY = { data: null, loading: false, error: '' };
export const useJob = (id) => useSyncExternalStore(subscribe, () => details.get(id) || EMPTY);

/* ---------- the plan (Heemo 2026-10-31: attic steps locked; office/agent set up → admin approves) ---------- */
// Step types the plan picks from (Heemo 2026-11-03). kind decides the date a step carries:
//   visit    someone goes to the house → booked date & time + who (auditor / crew / trade)
//   payment  money in → due date, then the day it was paid (Deposit / Final payment tick themselves)
//   office   our own work → due date, then the day it was done
// who = what the "who's coming" box asks for on a visit.
export const STEP_TYPES = [
  { type: 'deposit', label: 'Deposit', kind: 'payment' },
  { type: 'pre_audit', label: 'Enbridge pre-inspection', kind: 'visit', who: 'Auditor' },
  { type: 'removal', label: 'Removal day', kind: 'visit', who: 'Crew / installer' },
  { type: 'install', label: 'Install day', kind: 'visit', who: 'Crew / installer' },
  { type: 'roofer', label: 'Roofer visit', kind: 'visit', who: 'Roofer (company or name)' },
  { type: 'carpenter', label: 'Carpenter visit', kind: 'visit', who: 'Carpenter (company or name)' },
  { type: 'return_visit', label: 'Return visit / touch-up', kind: 'visit', who: 'Who’s going' },
  { type: 'post_audit', label: 'Enbridge post-inspection', kind: 'visit', who: 'Auditor' },
  { type: 'progress_payment', label: 'Progress payment', kind: 'payment' },
  { type: 'report', label: 'Before & after report', kind: 'office' },
  { type: 'rebate_paperwork', label: 'Rebate paperwork sent', kind: 'office' },
  { type: 'final_payment', label: 'Final payment', kind: 'payment' },
];
export const KIND_GROUPS = [
  ['visit', 'Visits'],
  ['payment', 'Payments'],
  ['office', 'Office'],
];
const TYPE = Object.fromEntries(STEP_TYPES.map((t) => [t.type, t]));
// a plan step's type: saved on newer steps; older ones are known by their key ("install", "custom-…" = Other)
export const stepType = (s) => TYPE[s.type] || TYPE[String(s.key || '').replace(/-\d+$/, '')] || { type: 'other', label: s.label, kind: 'visit', who: 'Who’s going' };
export const stepKind = (s) => stepType(s).kind;
export const isVisit = (s) => stepKind(s) === 'visit';
// visits to the house (Enbridge, crews, trades) — counted, not typed
export const visitCount = (steps) => steps.filter(isVisit).length;

// a new step of a type: the first one keeps the type as its key (so the old Zoho fields and the payment sync
// still find "deposit", "install"…); a second one of the same type gets "-2", "-3"…
export const newStep = (steps, type, label) => {
  const t = TYPE[type];
  const base = t ? type : 'custom';
  const taken = new Set(steps.map((s) => s.key));
  let key = t ? type : `custom-${Date.now().toString(36)}`;
  for (let n = 2; taken.has(key); n += 1) key = `${base}-${n}`;
  return { key, type: t ? type : 'other', label: t ? t.label : String(label || '').trim().slice(0, 60) };
};

// the default plan for a sold attic job
export const ATTIC_STEPS = ['deposit', 'pre_audit', 'install', 'post_audit', 'report', 'final_payment'].map((type) => ({ key: type, type, label: TYPE[type].label }));
const REBATE_KEYS = ['pre_audit', 'post_audit'];

// rebate on → the two Enbridge visits go back around the install day; off → they come out
export const withRebate = (steps, on) => {
  if (!on) return steps.filter((s) => !REBATE_KEYS.includes(s.key));
  let out = steps.slice();
  const at = (key) => out.findIndex((s) => s.key === key);
  if (at('pre_audit') < 0) {
    const i = at('install');
    out.splice(i < 0 ? out.length : i, 0, ATTIC_STEPS[1]);
  }
  if (at('post_audit') < 0) {
    const i = at('install');
    out.splice(i < 0 ? out.length : i + 1, 0, ATTIC_STEPS[3]);
  }
  return out;
};

const write = async (id, body) => {
  const out = await call({ ...body, jobId: id });
  await Promise.all([loadJob(id), refreshJobs()]);
  return out.message || 'Saved';
};
// visits are counted from the steps; who's coming is saved per visit when it's booked
export const savePlan = (id, { steps, rebate }, submit) =>
  write(id, {
    action: 'save_plan',
    steps: steps.map((s) => ({ key: s.key, type: stepType(s).type, label: s.label, kind: stepKind(s) })),
    visits: visitCount(steps) || null,
    rebate,
    submit,
  });
export const approvePlan = (id) => write(id, { action: 'approve' });
/* ---------- step buttons (approved plans): tick a step off, book a visit / set a due date, undo ---------- */
// a step that isn't the current one needs outOfOrder (the app asks first)
export const stepDone = (id, key, date, outOfOrder = false, pay = null) => write(id, { action: 'step_done', key, date, outOfOrder, ...(pay ? { pay } : {}) });
export const stepUndo = (id) => write(id, { action: 'step_undo' });
// visit → { at: 'YYYY-MM-DDTHH:MM', who }; payment / office → { due: 'YYYY-MM-DD' }
export const scheduleStep = (id, step, { at = '', who = '', due = '' } = {}) => {
  if (isVisit(step) ? !at : !due) throw new Error(isVisit(step) ? 'Pick a date and time' : 'Pick a due date');
  return write(id, { action: 'schedule', key: step.key, kind: stepKind(step), at, who: String(who || '').trim(), due });
};
// the one date line every step shows: done → ✓ the day; visit → 📅 booked time · who (orange "Not booked" until
// it is); payment / office → "Due …" once a due date is set. Older steps booked before 2026-11-03 keep their
// who on the Job (Auditor / Installer).
const day = (ymd) => new Date(`${ymd}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const dayTime = (iso) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
export const stepWho = (s, job = {}) => s.who || (['pre_audit', 'post_audit'].includes(s.key) ? job.auditor : s.key === 'install' ? job.installer : '') || '';
export const stepDate = (s, job = {}) => {
  const who = isVisit(s) ? stepWho(s, job) : '';
  if (s.status === 'done') return { text: `✓ ${s.date ? day(s.date) : 'Done'}${who ? ` · ${who}` : ''}`, tone: 'done' };
  if (isVisit(s)) return s.when ? { text: `📅 ${dayTime(s.when)}${who ? ` · ${who}` : ''}`, tone: 'booked' } : { text: 'Not booked', tone: 'unbooked' };
  return s.due ? { text: `Due ${day(s.due)}`, tone: 'booked' } : { text: 'No due date', tone: 'none' };
};
/* ---------- note / on hold / resume / cancel ---------- */
export const CANCEL_REASONS = ['Price', 'Went with another company', 'Not ready / timing', 'No response', 'Not a good candidate', 'Other'];
export const isOnHold = (j) => String((j && j.step) || '').startsWith('⏸');
export const addJobNote = (id, text) => {
  if (!String(text || '').trim()) throw new Error('Write the note first');
  return write(id, { action: 'add_note', text });
};
export const holdJob = (id, reason) => {
  if (!String(reason || '').trim()) throw new Error('Say why it’s on hold');
  return write(id, { action: 'hold', reason });
};
export const resumeJob = (id) => write(id, { action: 'resume' });
// job → Cancelled, its Deal → Closed Lost (+ reason); moving the Deal back to Won in Zoho reopens it
export const cancelJob = (id, lostReason, text = '') => {
  if (!CANCEL_REASONS.includes(lostReason)) throw new Error('Pick why it was cancelled');
  return write(id, { action: 'cancel', lostReason, text });
};

/* ---------- e-transfer / cash / cheque → Books payment (n8n "[C] Invoice 3 - Record Payment", any staff) ---------- */
// The payment sync then updates the Job (paid, balance, Deposit / Final payment steps) within seconds.
const PAYMENT_URL = process.env.REACT_APP_PAYMENT_URL;
export const PAYMENT_LIVE = !!PAYMENT_URL;
export const PAY_METHODS = [
  ['etransfer', 'e-Transfer'],
  ['cash', 'Cash'],
  ['cheque', 'Cheque'],
  ['other', 'Other'],
];
export const recordPayment = async (id, { amount, method, date, reference }) => {
  if (!(Number(amount) > 0)) throw new Error('Enter the amount');
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again');
  let res;
  try {
    res = await fetch(PAYMENT_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId: id, amount: Number(amount), method, date, reference }),
    });
  } catch {
    throw new Error('No connection — try again');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `Books didn’t save it (${res.status})`);
  // the sync writes paid / balance onto the Job a few seconds later
  setTimeout(() => { loadJob(id); refreshJobs(); }, 6000);
  return out.message || 'Payment recorded';
};
/* ---------- resend the same invoice (n8n "[C] Invoice 4 - Resend"): email, or a text when there's no email ---------- */
const RESEND_URL = process.env.REACT_APP_INVOICE_RESEND_URL;
export const RESEND_LIVE = !!RESEND_URL;
/* ---------- Mark done on a payment step (Deposit / Progress / Final payment, 2026-11-03) ---------- */
// invoice # + how they paid + amount + notes. The step is ticked with those details, then anything but a card
// payment (already in Books from the pay page) is recorded in Books against the job's invoice.
export const STEP_PAY_METHODS = [...PAY_METHODS.slice(0, 3), ['card', 'Card (paid online)'], ['other', 'Other']];
export const markPaid = async (job, step, { date, invoice, method, amount, notes }, outOfOrder = false) => {
  const toBooks = method !== 'card' && !!job.invoiceNumber;
  if (!STEP_PAY_METHODS.some(([m]) => m === method)) throw new Error('Pick how they paid');
  if (toBooks && !(Number(amount) > 0)) throw new Error('Enter the amount');
  const inv = String(invoice || '').trim();
  if (inv && job.invoiceNumber && inv.toUpperCase() !== job.invoiceNumber.toUpperCase()) throw new Error(`This job’s invoice is ${job.invoiceNumber}`);
  const pay = { method, amount: amount === '' || amount == null ? null : Number(amount), invoice: inv || job.invoiceNumber || '', notes: String(notes || '').trim() };
  const ticked = await stepDone(job.id, step.key, date, outOfOrder, pay);
  if (!toBooks || !PAYMENT_LIVE) return ticked;
  try {
    return await recordPayment(job.id, { amount: Number(amount), method, date, reference: pay.notes.slice(0, 50) });
  } catch (err) {
    throw new Error(`${step.label} ticked, but Books didn’t record the payment: ${err.message}. Use “+ Record e-transfer / cash / cheque” under Payment.`);
  }
};
export const resendInvoice = async (id) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again');
  let res;
  try {
    res = await fetch(RESEND_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId: id }),
    });
  } catch {
    throw new Error('No connection — try again');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `It didn’t go out (${res.status})`);
  loadJob(id);
  return out.message || 'Invoice re-sent';
};
export const sendBackPlan = (id, reason) => {
  if (!String(reason || '').trim()) throw new Error('Say what needs changing');
  return write(id, { action: 'send_back', reason });
};
